import { Heart, Sparkles, TrendingUp, Shield } from "lucide-react";

// Payout configuration - easily editable
export const PAYOUT_CONFIG = {
  preventativeCare: {
    title: "Aspire Preventative Wellness",
    description: "Aspire Preventive Wellness' non-insurance program brings tax savings and wellness benefits to employees and businesses at a net neutral cost.",
    icon: Heart,
    sizeLabels: {
      small: "$2,000/mo",
      medium: "$20,000/mo",
      large: "$100,000/mo"
    },
    sizeDescriptions: {
      small: "100 Employees",
      medium: "1,000 Employees",
      large: "5,000 Employees"
    },
    payouts: {
      small: { monthly: 2000, year1: 24000, year5: 120000, year10: 240000 },
      medium: { monthly: 20000, year1: 240000, year5: 1200000, year10: 2400000 },
      large: { monthly: 100000, year1: 1200000, year5: 6000000, year10: 12000000 }
    }
  },
  technologySolutions: {
    title: "Aspire Technology Solutions",
    description: "Aspire Technology Audit delivers a free global audit powered by 900+ suppliers—helping businesses cut tech costs while connecting them with the top AI solutions in the market.",
    icon: Sparkles,
    sizeLabels: {
      small: "$2,000/mo",
      medium: "$10,000/mo",
      large: "$100,000/mo"
    },
    sizeDescriptions: {
      small: "$50k Monthly Client Billing",
      medium: "$250k Monthly Client Billing",
      large: "$5M Monthly Client Billing"
    },
    payouts: {
      small: { monthly: 2000, year1: 24000, year5: 120000, year10: 240000 },
      medium: { monthly: 10000, year1: 120000, year5: 600000, year10: 1200000 },
      large: { monthly: 100000, year1: 1200000, year5: 6000000, year10: 12000000 }
    }
  },
  healthcareRCM: {
    title: "Aspire Partners Healthcare Revenue Cycle Management",
    description: "Aspire Healthcare's RCM helps any healthcare business collect up to 98% of accounts receivable—without long-term contracts or replacing their current billing team.",
    icon: TrendingUp,
    sizeLabels: {
      small: "$1,500/mo",
      medium: "$12,500/mo",
      large: "$200,000/mo"
    },
    sizeDescriptions: {
      small: "Solo Practitioner",
      medium: "Regional Hospitals, Clinics, etc...",
      large: "Major Hospitals, Multi-Practices, etc..."
    },
    payouts: {
      small: { monthly: 1500, year1: 18000, year5: 90000, year10: 180000 },
      medium: { monthly: 12500, year1: 150000, year5: 750000, year10: 1500000 },
      large: { monthly: 200000, year1: 2400000, year5: 12000000, year10: 24000000 }
    }
  },
  legalGuard: {
    title: "Aspire Legal Guard",
    description: "Aspire Legal Guard brings voluntary legal and ID protection benefits to employers and their employees.",
    icon: Shield,
    sizeLabels: {
      small: "$10k upfront + Annual Renewals",
      medium: "$100k upfront + Annual Renewals",
      large: "$500k upfront + Annual Renewals"
    },
    sizeDescriptions: {
      small: "100 Employees",
      medium: "1,000 Employees",
      large: "5,000 Employees"
    },
    payouts: {
      small: { monthly: 833.33, year1: 10000, year5: 50000, year10: 100000 },
      medium: { monthly: 8333.33, year1: 100000, year5: 500000, year10: 1000000 },
      large: { monthly: 41666.67, year1: 500000, year5: 2500000, year10: 5000000 }
    }
  }
};

// Format currency with abbreviation for large values
export const formatCurrency = (value) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Format full currency (no abbreviation)
export const formatFullCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Get motivational message based on total
export const getMotivationalMessage = (total) => {
  if (total === 0) return "";
  if (total < 50000) return "You may be sitting on a meaningful new income stream.";
  if (total < 500000) return "Your network could be worth a serious recurring revenue opportunity.";
  return "You may already know enough businesses to build substantial long-term residual income.";
};

// Calculate totals from calculator values
export const calculateTotals = (calculatorValues) => {
  let monthly = 0, year1 = 0, year5 = 0, year10 = 0;
  let totalBusinesses = 0;

  Object.keys(PAYOUT_CONFIG).forEach(key => {
    const config = PAYOUT_CONFIG[key];
    const values = calculatorValues[key];
    
    ['small', 'medium', 'large'].forEach(size => {
      const count = values[size] || 0;
      if (count > 0) {
        totalBusinesses += count;
        monthly += count * config.payouts[size].monthly;
        year1 += count * config.payouts[size].year1;
        year5 += count * config.payouts[size].year5;
        year10 += count * config.payouts[size].year10;
      }
    });
  });

  return { monthly, year1, year5, year10, totalBusinesses };
};
